import React, { useEffect, useState } from 'react';
import { Wrapper } from './style';
import { DatePicker, message, Select, Space, Alert, Button } from 'antd';
import { Link } from 'react-router-dom';
import { MakeApiCall } from '../../../apis';
import AllAlerts from './lib/allAlerts';
import EmailAlerts from './lib/alerts';
import { GetMarketplaceDropdown, GetMarketplaceList } from '../../../config';
import Settings from './lib/settings';
import AddNewAlerts from './lib/AddNewAlerts';

const { Option } = Select;
const { RangePicker } = DatePicker;

function Alerts({ userData }) {
    // --- Connection Logic ---
    const isSpApiConnected = userData?.user?.user_data?.is_amz_sp_api_connected === 1;
    const isAdsApiConnected = userData?.user?.user_data?.is_amz_ads_connected === 1;

    const [activeTab, setActiveTab] = useState('allAlerts');
    const [totalPage, setTotalPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);

    const [allAlerts, setAllAlerts] = useState([]);
    const [alerts, setAlerts] = useState([]);

    const [marketplaceList, setMarketplaceList] = useState([]);
    const [selectedMarketplace, setSelectedMarketplace] = useState(null);
    const [currencyList, setCurrencyList] = useState([]);
    const [selectedCurrencySign, setSelectedCurrencySign] = useState('');

    const [isModalOpen, setIsModalOpen] = useState(false);

    const [asinList, setAsinList] = useState([]);
    const [selectedAsins, setSelectedAsins] = useState([]);

    const [editing, setEditing] = useState(false);
    const [fetchAlertSettings, setFetchAlertSettings] = useState(() => () => {});

    const [filters, setFilters] = useState({
        page: 1,
        'per-page': 20,
        name: '',
        marketplace_id: null,
    });

    const [alertCategories, setAlertCategories] = useState([]);
    const [alertDataMap, setAlertDataMap] = useState({
        types: {},
        receive_on: [],
        logicTypeMap: {}
    });

    const fetchProductList = async () => {
        if (!isSpApiConnected) return; // Added guard
        setLoading(true);
        try {
            const response = await MakeApiCall(
                'get-products?no_pagination=true',
                'get',
                null,
                true,
                {},
                userData?.token
            );
            if (response?.status) {
                const records = response?.data?.records || [];
                const asins = records.map(item => ({
                    asin: item.asin,
                    name: item.item_name,
                    image: item.item_image,
                }));
                setAsinList(asins);
            } else {
                message.warning(response?.message || "Failed to load ASINs");
            }
        } catch (error) {
            message.error('Error fetching products.');
        } finally {
            // setLoading(false);
        }
    };


    const GetMarketplace = async () => {
        if (!isSpApiConnected) return; // Added guard
        const marketplace = await GetMarketplaceDropdown();

        if (marketplace?.marketplace_list?.length > 0) {
            const selectedMarketplaceEntry = Object.entries(marketplace.selected_marketplace || {})[0];
            const [marketplaceId, marketplaceName] = selectedMarketplaceEntry || [];

            const firstMarketplace = marketplace.marketplace_list.find(
                (m) => m.id === marketplaceId
            );

            setMarketplaceList(marketplace.marketplace_list);
            setSelectedMarketplace(firstMarketplace);

            setFilters((prev) => ({
                ...prev,
                marketplace_id: marketplaceId,
            }));
        } else {
            setLoading(false);
        }
    };

    const fetchAlertMaster = async () => {
        if (!isSpApiConnected) return; // Added guard
        setLoading(true);
        try {
            const response = await MakeApiCall(
                "get-alert-master-data",
                "get",
                null,
                true,
                {},
                userData?.token
            );
            if (response?.status) {
                const { alert_catalogs_data, alert_type, alert_receive_on, alert_logic_type } = response.data;

                const categories = Object.entries(alert_catalogs_data).map(([key, label]) => ({
                    label,
                    value: key
                }));

                setAlertCategories(categories);

                setAlertDataMap({
                    types: alert_type,
                    receive_on: alert_receive_on,
                    logicTypeMap: alert_logic_type
                });

            } else {
                message.warning(response?.message || "Failed to load alert data");
            }
        } catch (error) {
            message.error("Error fetching alert master data.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isSpApiConnected) { // Added guard
            GetMarketplace();
            fetchProductList();
            fetchAlertMaster();
        } else {
            setLoading(false);
        }
    }, [isSpApiConnected]); // Re-run if connection status changes


    const handleSubmit = async (values) => {
        try {
            setLoading(true);
            const response = await MakeApiCall(
                editing
                    ? `update-alert/${editing.id}`
                    : "add-alert",
                editing ? "post" : "post",
                values,
                true,
                {},
                userData?.token
            );
            if (response?.status) {
                message.success(editing ? "Alert updated successfully!" : "Alert added successfully!");
                setIsModalOpen(false)
                setActiveTab('settings');
                fetchAlertSettings();
            } else {
                message.error(response?.message || (editing ? "Failed to update alert." : "Failed to add alert."));
            }
        } catch (error) {
            console.error("Error adding Alert:", error);
            message.error("An unexpected error occurred.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Wrapper>
            {/* Conditional Banners */}
            {!isSpApiConnected && (
                <Alert
                    message={<span className="fw-bolder fs-5">Action Required: Connect Marketplace</span>}
                    description={
                        <div>
                            <p className="mb-2">Your Amazon Selling Partner API is not connected. This is required to manage and sync your business expenses.</p>
                            <Link to="/settings/marketplace-credentials?tab=spApi&creds=true">
                                <Button type="primary" danger size="small">Connect SP-API</Button>
                            </Link>
                        </div>
                    }
                    type="error"
                    showIcon
                    className="mb-4 p-6"
                />
            )}

            {/* 2. Ads API Banner (Warning Level) */}
            {!isAdsApiConnected && (
                <Alert
                    message={<span className="fw-bolder fs-6">Amazon Advertising Not Connected</span>}
                    description={
                        <div>
                            <p className="mb-2">PPC and Advertising-related expenses will not be accurately reflected in your metrics.</p>
                            {/* Redirecting to advertising tab and triggering the auto-modal logic we discussed */}
                            <Link to="/settings/marketplace-credentials?tab=advertising&creds=true">
                                <Button type="primary" danger size="small">Connect Ads API</Button>
                            </Link>
                        </div>
                    }
                    type="error"
                    showIcon
                    closable={isSpApiConnected} // Only allow closing if the main API is connected
                    className="mb-4 p-6"
                />
            )}

            <div style={{ position: 'relative' }}>

                {/* Overlay for SP-API disconnection */}
                {!(isSpApiConnected && isAdsApiConnected) && (
                    <div style={{
                        position: 'absolute',
                        top: 0, left: -10, right: -10, bottom: 0,
                        backgroundColor: 'rgba(255, 255, 255, 0.5)',
                        backdropFilter: 'blur(5px)',
                        zIndex: 10,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '12px'
                    }}>
                        <div className="text-center bg-white p-8 shadow-sm rounded border">
                            <i className="ki-outline ki-lock-2 fs-3x text-muted mb-4"></i>
                            <h3 className="text-gray-800">Features Locked</h3>
                            <p className="text-muted">Connect your Amazon account to unlock alerts.</p>
                        </div>
                    </div>
                )}

                <div class="mb-10 mx-n9 px-10 py-5 bg-white filter ">
                    <div class="position-relative d-flex wrap justify-content-between">
                        <ul class="nav nav-tabs nav-line-tabs nav-stretch fs-6 d-inline-flex">
                            <li class="nav-item">
                                <Link class={`nav-link ${activeTab === 'allAlerts' ? 'active' : ''}`} data-bs-toggle="tab" onClick={() => isSpApiConnected && setActiveTab('allAlerts')}>All Alerts</Link>
                            </li>
                            <li class="nav-item">
                                <Link class={`nav-link ${activeTab === 'alerts' ? 'active' : ''}`} data-bs-toggle="tab" onClick={() => isSpApiConnected && setActiveTab('alerts')}>Email/WhatsApp Alerts</Link>
                            </li>
                            <li class="nav-item">
                                <Link class={`nav-link ${activeTab === 'settings' ? 'active' : ''}`} data-bs-toggle="tab" onClick={() => isSpApiConnected && setActiveTab('settings')}>Settings</Link>
                            </li>

                        </ul>
                        <button className="btn btn-danger fs-7 fw-bold" disabled={!isSpApiConnected} onClick={() => setIsModalOpen(true)}>Add New Alerts</button>
                    </div>
                </div>

                <div className="mb-5 d-flex gap-4 flex-wrap filters">
                    <Space wrap>
                        <Select
                            mode="multiple"
                            placeholder="Select ASIN"
                            style={{ minWidth: 250 }}
                            size="large"
                            value={selectedAsins}
                            onChange={setSelectedAsins}
                            loading={loading}
                            disabled={!isSpApiConnected}
                            optionLabelProp="value"
                            maxTagCount={'responsive'}
                        >
                            {asinList.map(item => (
                                <Option key={item.asin} value={item.asin}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <img
                                            src={item.image || 'https://app.sellerview.ai/sellerview_api/images/no-product-image.png'}
                                            alt={item.name}
                                            className="w-50px"
                                        />
                                        <div>
                                            <div style={{ fontWeight: '500' }}>{item.name}</div>
                                            <div style={{ fontSize: '13px', color: '#888' }}>{item.asin}</div>
                                        </div>
                                    </div>
                                </Option>
                            ))}
                        </Select>
                        <Select
                            placeholder="Select Category"
                            style={{ minWidth: 175 }}
                            size="large"
                            loading={loading && alertCategories.length === 0}
                            allowClear
                            disabled={!isSpApiConnected}
                        >
                            {alertCategories.map(cat => (
                                <Option key={cat.value} value={cat.value}>
                                    {cat.label}
                                </Option>
                            ))}
                        </Select>

                        <Select
                            placeholder="Select Severity"
                            style={{ minWidth: 175 }}
                            size="large"
                            allowClear
                            disabled={!isSpApiConnected}
                        >
                            <Option value="1">Minor</Option>
                            <Option value="2">Major</Option>
                            <Option value="3">Critical</Option>
                        </Select>

                        <Select
                            className="w-200px"
                            size="large"
                            placeholder="Select Marketplace"
                            options={GetMarketplaceList(marketplaceList)}
                            value={selectedMarketplace?.id}
                            disabled={!isSpApiConnected}
                            onChange={(e) => {
                                const selected = marketplaceList.find((m) => m.id === e);
                                setSelectedMarketplace(selected);

                                const apiObj = {
                                    ...filters,
                                    marketplace_id: e,
                                };
                                setFilters(apiObj);

                                const apiObj_ = {
                                    ...apiObj,
                                    page: 1,
                                    "per-page": 20,
                                };

                                setLoading(true);
                            }}
                        />
                        <RangePicker
                            size="large"
                            disabled={!isSpApiConnected}
                            className="form-control d-flex fs-7 h-40px w-250px"
                            placeholder={['Start date', 'End date']}
                            suffixIcon={<i className="ki-duotone ki-calendar-8 fs-1 mb-n1 me-0"><span className="path1"></span><span className="path2"></span><span className="path3"></span><span className="path4"></span><span className="path5"></span><span class="path6"></span></i>}
                        />
                    </Space>
                </div>

                {activeTab === 'allAlerts' && <AllAlerts userData={userData} list={allAlerts} loading={loading}/>}
                {activeTab === 'alerts' && <EmailAlerts list={alerts} loading={loading}/>}
                {activeTab === 'settings' && <Settings exposeFetcher={setFetchAlertSettings} setIsModalOpen={setIsModalOpen} setEditing={setEditing} userData={userData} loading={loading}/>}

                <AddNewAlerts
                    isModalVisible={isModalOpen}
                    handleCancel={() => setIsModalOpen(false)}
                    handleSubmit={handleSubmit}
                    marketplaceList={marketplaceList}
                    editing={editing}
                    setSelectedAsins={setSelectedAsins}
                    selectedAsins={selectedAsins}
                    asinList={asinList}
                    userData={userData}
                    alertCategories={alertCategories}
                    alertDataMap={alertDataMap}
                />
            </div>
        </Wrapper>
    )
}

export default Alerts;